import JSZip from "jszip";

export type ParsedSlide = {
  slideId: string;
  textContent: string;
  contentHash: string;
};

const SLD_ID_TAG_RE = /<p:sldId\b[^>]*\/?>/g;
const REL_TAG_RE = /<Relationship\b[^>]*\/?>/g;
const TEXT_RE = /<a:t[^>]*>([^<]*)<\/a:t>/g;

const attrValue = (tag: string, name: string): string | undefined => {
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  // Bare names must not match the suffix of a prefixed attr (e.g. "id" vs "r:id").
  const pattern = name.includes(":")
    ? `\\b${escaped}="([^"]+)"`
    : `(?:^|[\\s])${escaped}="([^"]+)"`;
  return tag.match(new RegExp(pattern))?.[1];
};

const SKIP_REL_TYPE_SUFFIXES = [
  "/slideLayout",
  "/slideMaster",
  "/notesMaster",
  "/handoutMaster",
  "/theme",
  "/themeOverride",
  "/commentAuthors",
  "/comments",
  "/modernComment",
  "/tags",
] as const;

const decodeXmlEntities = (s: string): string =>
  s
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, "&");

const bytesToHex = (bytes: ArrayBuffer): string => {
  const view = new Uint8Array(bytes);
  let out = "";
  for (let i = 0; i < view.length; i++) {
    out += (view[i] ?? 0).toString(16).padStart(2, "0");
  }
  return out;
};

const sha256Hex = async (parts: Uint8Array[]): Promise<string> => {
  let total = 0;
  for (const part of parts) total += part.byteLength;
  const joined = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    joined.set(part, offset);
    offset += part.byteLength;
  }
  const digest = await crypto.subtle.digest("SHA-256", joined);
  return bytesToHex(digest);
};

const encodeUtf8 = (s: string): Uint8Array => new TextEncoder().encode(s);

const normalizeZipPath = (path: string): string => {
  const trimmed = path.startsWith("/") ? path.slice(1) : path;
  const out: string[] = [];
  for (const seg of trimmed.split("/")) {
    if (seg === "" || seg === ".") continue;
    if (seg === "..") {
      out.pop();
      continue;
    }
    out.push(seg);
  }
  return out.join("/");
};

const resolveRelTarget = (sourcePartPath: string, target: string): string => {
  if (target.startsWith("/")) return normalizeZipPath(target);
  const slash = sourcePartPath.lastIndexOf("/");
  const baseDir = slash >= 0 ? sourcePartPath.slice(0, slash + 1) : "";
  return normalizeZipPath(`${baseDir}${target}`);
};

const relsPathFor = (partPath: string): string => {
  const slash = partPath.lastIndexOf("/");
  if (slash < 0) return `_rels/${partPath}.rels`;
  return `${partPath.slice(0, slash)}/_rels/${partPath.slice(slash + 1)}.rels`;
};

const shouldFollowRelType = (type: string): boolean =>
  !SKIP_REL_TYPE_SUFFIXES.some((suffix) => type.endsWith(suffix));

type Rel = { id: string; type: string; target: string };

const parseRels = (relsXml: string): Rel[] => {
  const out: Rel[] = [];
  for (const tag of relsXml.matchAll(REL_TAG_RE)) {
    const raw = tag[0] ?? "";
    const id = attrValue(raw, "Id");
    const type = attrValue(raw, "Type");
    const target = attrValue(raw, "Target");
    if (id && type && target) out.push({ id, type, target });
  }
  return out;
};

const collectSlideFingerprintParts = async (
  zip: JSZip,
  slidePath: string,
): Promise<Uint8Array[]> => {
  const parts: Uint8Array[] = [];
  const visited = new Set<string>();
  const queue: string[] = [normalizeZipPath(slidePath)];

  while (queue.length > 0) {
    const partPath = queue.shift();
    if (partPath === undefined || visited.has(partPath)) continue;
    visited.add(partPath);

    const entry = zip.file(partPath);
    if (!entry) continue;
    const bytes = await entry.async("uint8array");
    parts.push(encodeUtf8(`part:${partPath}\n`), bytes);

    const relsPath = relsPathFor(partPath);
    if (visited.has(relsPath)) continue;
    const relsEntry = zip.file(relsPath);
    if (!relsEntry) continue;
    visited.add(relsPath);
    const relsXml = await relsEntry.async("string");
    parts.push(encodeUtf8(`rels:${relsPath}\n`), encodeUtf8(relsXml));

    for (const rel of parseRels(relsXml)) {
      if (!shouldFollowRelType(rel.type)) continue;
      queue.push(resolveRelTarget(partPath, rel.target));
    }
  }

  return parts;
};

const extractText = (slideXml: string): string => {
  const runs: string[] = [];
  for (const match of slideXml.matchAll(TEXT_RE)) {
    const [, text] = match;
    if (typeof text === "string") runs.push(decodeXmlEntities(text));
  }
  return runs.join("\n");
};

const parseSldIds = (
  presentationXml: string,
): { slideId: string; rId: string }[] => {
  const ordered: { slideId: string; rId: string }[] = [];
  for (const tag of presentationXml.matchAll(SLD_ID_TAG_RE)) {
    const raw = tag[0] ?? "";
    const slideId = attrValue(raw, "id");
    const rId = attrValue(raw, "r:id");
    if (slideId && rId) ordered.push({ slideId, rId });
  }
  return ordered;
};

export const parseDeck = async (bytes: Uint8Array): Promise<ParsedSlide[]> => {
  const zip = await JSZip.loadAsync(bytes);

  const presentationEntry = zip.file("ppt/presentation.xml");
  const relsEntry = zip.file("ppt/_rels/presentation.xml.rels");
  if (!presentationEntry || !relsEntry) return [];

  const presentationXml = await presentationEntry.async("string");
  const relsXml = await relsEntry.async("string");

  const relMap = new Map<string, string>();
  for (const rel of parseRels(relsXml)) {
    relMap.set(rel.id, rel.target);
  }

  const out: ParsedSlide[] = [];
  for (const { slideId, rId } of parseSldIds(presentationXml)) {
    const target = relMap.get(rId);
    if (typeof target !== "string") continue;
    const path = normalizeZipPath(
      target.startsWith("/") ? target.slice(1) : `ppt/${target}`,
    );
    const slideEntry = zip.file(path);
    if (!slideEntry) continue;
    const slideXml = await slideEntry.async("string");
    const fingerprintParts = await collectSlideFingerprintParts(zip, path);
    out.push({
      slideId,
      textContent: extractText(slideXml),
      contentHash: await sha256Hex(fingerprintParts),
    });
  }
  return out;
};
